import {Inject, Injectable, Injector} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {
    ITCIAppCustomizationProvider,
    ITCIAppCustomizationRequest,
    ITCIAppCustomizationResponse,
    ITCIAppEnvControlProvider,
    ITCIAppEnvControlProviderRequest,
    ITCIAppEnvControlProviderResponse,
    ITCIApplication,
    ITCIApplicationCreateProvider,
    ITCIApplicationCreateRequest,
    ITCIApplicationCreateResponse,
    ITCIApplicationDeleteProvider,
    ITCIApplicationDeleteRequest,
    ITCIApplicationDeleteResponse,
    ITCIApplicationDeploymentMiniDetails,
    ITCIApplicationDeploymentProvider,
    ITCIApplicationDeployRequest,
    ITCIApplicationDeployResponse,
    ITCIApplicationMonitoringProvider,
    ITCIApplicationMonitoringResponse,
    ITCIApplicationsMonitoringRequest,
    ITCIApplicationUpdateProvider,
    ITCIApplicationUpdateRequest,
    ITCIApplicationUpdateResponse,
    ITCIConnection,
    ITCIConnectionRequest,
    ITCIConnectionResponse,
    ITCIConnectionServiceProvider,
    ITCIConnector, ITCIApplicationExecHistoryProvider,
    ITCIMarketplaceConnector,
    ITCIMarketplaceConnectorRequest,
    ITCIMarketplaceConnectorResponse,
    ITCIMarketplaceServiceProvider,
    ITCIMonitoringInstance,
    ITCISupplement,
    ITCISupplementConnector,
    ITCISupplementServiceProvider,
    ITCITenantComponentInput,
    ITCITenantSupplementRequest,
    ITCITenantSupplementResponse,
    ITCIValidationResult,
    TCI_APP_CUSTOMIZATION_TYPE,
    TCI_APP_MONITORING_REQUEST_TYPE,
    TCI_ENV_CTRL_ACTION_TYPE,
    TCI_TENANT_SERVICE_PROVIDER_TYPE,
    TCI_UPDATE_ACTION_TYPE,
    TCITenantAppType,
    ITCIExecHistoryResponse,
    ITCIExecHistoryProcessResponse,
    TCIExecHistoryStatus,
    TCIExecHistorySortableColumn,
    TCIMarketplaceSupportedTechs,
    ITCIExecHistoryProcessListResponse,
    ITCIExecHistoryDetailResponse,
    TCITenantServiceProvider
} from "@tci/common";
import {Http} from "@angular/http";

/*****************************************************************************************************/
/**
 * interface ITCIAppCustomizationRequest
 */
export abstract class BaseAppCustomizationRequest implements ITCIAppCustomizationRequest {
    /**
     * type of customization area
     */
    type: TCI_APP_CUSTOMIZATION_TYPE;
    /**
     * any associated ng-custoinput data
     */
    data?: any;
}

/**
 * BaseAppCustomizationResponse
 */
// export class BaseAppCustomizationResponse implements ITCIAppCustomizationResponse, ITCIAppListCustomization {
//     [id: string]: ITCIAppCustomizationParam;
//
//     success: boolean;
//
//     /**
//      * get Customizations for a given type
//      * @param type
//      */
//     getCustomizationParam(_type: TCI_APP_CUSTOMIZATION_TYPE): ITCIAppCustomizationParam {
//         return this[_type as string];
//     }
//
//     /**
//      * set Customization Param
//      * @param type
//      * @param param
//      */
//     setCustomizationParam(_type: TCI_APP_CUSTOMIZATION_TYPE, param: ITCIAppCustomizationParam) {
//         this[_type as string] = param;
//     }
//
// }

@Injectable()
export abstract class BaseTCIAppCustomizationProvider extends TCITenantServiceProvider implements ITCIAppCustomizationProvider {
    constructor() {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_CUSTOMIZE);
    }

    /**
     * get customizations
     * @param input
     */
    abstract getCustomizations(input: ITCIAppCustomizationRequest): Observable<ITCIAppCustomizationResponse>;
}

/**
 * Abstract class for implementing Tenant import code
 * This class is discoverable through the ITCIServiceLoader service
 */
@Injectable()
export abstract class BaseTCIApplicationMonitoringProvider extends TCITenantServiceProvider implements ITCIApplicationMonitoringProvider {

    /**
     * @constructor
     */
    constructor(@Inject(Injector) protected _injector?: Injector, @Inject(Http) protected _http?: Http) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_MONITORING);
    }

    /**
     * Start APP_LIST_HEADER subscription
     * @param input
     */
    abstract startAppMonitoringSubscription(input: ITCIApplicationsMonitoringRequest): Observable<boolean>;

    /**
     * Stop APP_LIST_HEADER subscription
     * @param input
     */
    abstract stopAppMonitoringSubscription(input: ITCIApplicationsMonitoringRequest): Observable<boolean>;

    /**
     * get Monitoring subject by type
     * @param type
     */
    abstract getAppMonitoringSubject(type: TCI_APP_MONITORING_REQUEST_TYPE): Subject<ITCIApplicationMonitoringResponse>;

    /**
     * UI check to show Metrics Collection Dynamic component
     * Tenants can put their metrics toggles in this dynamic component
     * The tenants need to maintain state of the toggles on the tenant implementation
     * of the provider and TCI base code will not maintain them as it is not aware
     * of how many and which toggles
     * @param input
     */
    abstract canShowMetricsCollection(input: ITCIApplicationsMonitoringRequest): Observable<boolean>;

    /**
     * UI check to show the app instance selector drop down
     * for a given app id
     */
    abstract canShowInstanceSelector(input: ITCIApplicationsMonitoringRequest): Observable<boolean>;

    /**
     * Initial data for app instances to populate the InstanceSelector, dynamically updated
     * later using monitoring subscription
     * @param input
     */
    abstract getAppInstances(input: ITCIApplicationsMonitoringRequest): Observable<ITCIApplicationMonitoringResponse>;

    /**
     * Initialize the observation of an instance
     * @param instance
     */
    abstract setSelectedInstance(instance: ITCIMonitoringInstance): void;

    /**
     * Return the intance that is being observated
     */
    abstract getSelectedInstance(): Observable<ITCIMonitoringInstance>;

    /**
     * Perform refresh for data based on TCI_APP_MONITORING_REQUEST_TYPE
     * @param input
     */
    abstract refresh(input: ITCIApplicationsMonitoringRequest): Observable<ITCIApplicationMonitoringResponse>;

}

/**
 * Abstract class for implementing Tenant import code
 * This class is discoverable through the ITCIServiceLoader service
 */
@Injectable()
export abstract class BaseTCIApplicationCreateProvider extends TCITenantServiceProvider implements ITCIApplicationCreateProvider {

    /**
     * @constructor
     */
    constructor(@Inject(Injector) private _injector?: Injector, @Inject(Http) private _http?: Http) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_CREATE);
    }


    /**
     * Check before calling importApplication
     * @param input
     */
    abstract canCreate(input: ITCIApplicationCreateRequest): Observable<boolean | ITCIValidationResult>;

    /**
     * Perform validation before import
     * @param input
     */
    abstract validate(input: ITCIApplicationCreateRequest): Observable<ITCIValidationResult>;

    /**
     * Perform import
     * @param input
     * @param returnUrl
     */
    abstract createApplication(input: ITCIApplicationCreateRequest, returnUrl?: string): Observable<ITCIApplicationCreateResponse>;

    /**
     * Determines if the tenant is providing an External wizard.
     */
    isExternalWizard(): Observable<boolean> {
        return Observable.of(false);
    }

    /**
     * Get the external wizard URL from tenant
     * @param input
     */
    getExternalWizardUrl(input: ITCIApplicationCreateRequest): Observable<string> {
        return Observable.of("");
    }
}

/*****************************************************************************************************/
/**
 *  Abstract class for implementing the application deployment provider
 */
@Injectable()
export abstract class BaseTCIApplicationDeleteProvider extends TCITenantServiceProvider implements ITCIApplicationDeleteProvider {
    /**
     * @constructor
     */
    constructor(@Inject(Injector) private _injector?: Injector, @Inject(Http) private _http?: Http) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DELETE);
    }

    /**
     * get injector
     * @protected
     */
    protected getInjector(): Injector {
        return this._injector;
    }

    /**
     * get http
     * @protected
     */
    protected getHttp(): Http {
        return this._http;
    }

    /**
     *
     * @param input
     */
    abstract canDeleteApplication(input: ITCIApplicationDeleteRequest): Observable<boolean>;

    /**
     *
     * @param input
     */
    abstract deleteApplication(input: ITCIApplicationDeleteRequest): Observable<ITCIApplicationDeleteResponse>;
}

export abstract class BaseTCIApplicationUpdateProvider extends TCITenantServiceProvider implements ITCIApplicationUpdateProvider {
    /**
     * @constructor
     */
    constructor(@Inject(Injector) private _injector?: Injector, @Inject(Http) private _http?: Http) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_UPDATE);
    }

    /**
     *
     * Enable/Disable the name and description UI fields according to the boolean output
     * @param input
     */
    abstract canUpdateApplication(input: ITCIApplicationUpdateRequest): Observable<boolean>;

    /**
     *
     * Request application name/description update
     * @param input
     */
    abstract updateApplication(input: ITCIApplicationUpdateRequest): Observable<ITCIApplicationUpdateResponse>;

    /**
     * Validate the files selected by the user
     * The validation result can be an error or warning
     * @param input
     */
    abstract validate(input: ITCIApplicationUpdateRequest): Observable<ITCIValidationResult>;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    abstract startUpdateSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    abstract stopUpdateSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Disconnected Components that take actions related to a application name desc update
     * which impacts the Component state need to enclose the
     * actions within the callback so the update provider can let
     * other those dependencies get notified on the completion of the action
     * for performing refresh or any other action
     * @param input
     * @param callback
     */
    abstract pushAppChanges(input: ITCIApplicationUpdateRequest,
                            callback: (cinput: ITCIApplicationUpdateRequest)
                                => Observable<ITCIApplicationUpdateResponse>): Observable<ITCIApplicationUpdateResponse>;

    /**
     * Return state if the provider is busy waiting for the pushAppChanges to finish
     * @param input
     */
    abstract isBusy(input: ITCIApplicationUpdateRequest): Observable<boolean>;

    /**
     * Set the editing field state
     * @param isDirty True: Is dirty, False: Is pristine
     */
    abstract setDirty(isDirty: boolean, type: TCI_UPDATE_ACTION_TYPE): void;
}

/*****************************************************************************************************/
/**
 * Abstract class for implementing Tenant import code
 * This class is discoverable through the ITCIServiceLoader service
 */
export abstract class BaseTCIAppEnvControlProvider implements ITCIAppEnvControlProvider {
    protected tabdata: { [key: string]: any } = <{ [key: string]: any }>{};
    protected changeSubject$ = new Subject<TCI_ENV_CTRL_ACTION_TYPE>();


    getStatusSubject(): Subject<TCI_ENV_CTRL_ACTION_TYPE> {
        return this.changeSubject$;
    }

    /**
     * get stored tab data
     * @param key
     */
    getTabData(key: TCI_ENV_CTRL_ACTION_TYPE): any {
        return this.tabdata[key];
    }

    /**
     * store tab data
     * @param key
     * @param data
     */
    setTabData(key: TCI_ENV_CTRL_ACTION_TYPE, data: any) {
        this.tabdata[key] = data;
        this.getStatusSubject().next(key);
    }

    /**
     * initialize the component
     */
    abstract initApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    abstract canPushApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle app properties update
     * @param request
     */
    abstract pushApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize the component
     */
    abstract initEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    abstract canPushEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle engine properties update
     * @param request
     */
    abstract pushEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize the component
     */
    abstract initHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    abstract canPushHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle hybrid connectivity update
     * @param request
     */
    abstract pushHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize the component
     */
    abstract initAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    abstract canPushAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle app vpn update
     * @param request
     */
    abstract pushAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize AutoScale component
     */
    abstract initAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    abstract canPushAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle app autoscale update
     * @param request
     */
    abstract pushAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;
}

/*****************************************************************************************************/
/*                        App Deployment                                                             */

/*****************************************************************************************************/
/**
 *  Abstract class for implementing the application deployment provider
 */
@Injectable()
export abstract class BaseTCIApplicationDeploymentProvider extends TCITenantServiceProvider implements ITCIApplicationDeploymentProvider {

    /**
     * @constructor
     */
    constructor(@Inject(Injector) private _injector?: Injector, @Inject(Http) private _http?: Http) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DEPLOY);
    }

    /**
     * Tell TCI deployer UI whether to show confirmation popup
     * @param input
     */
    abstract canShowConfirmationDialog(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * Show Start/Stop widgets from being displayed
     * @param input
     * @return @Observable<boolean>
     */
    abstract canShowStartStop(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Get the start state information whether the application can be started
     * @param input
     * @return @Observable<boolean>
     */
    abstract canStart(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * starts the application
     * @param input
     * @return Observable<ITCIApplicationDeployResponse>
     */
    abstract start(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * Get the stop state information whether the application can be stopped
     * @param input
     * @return @Observable<boolean>
     */
    abstract canStop(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * stops the application
     * @param input
     * @return Observable<ITCIApplicationDeployResponse>
     */
    abstract stop(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * This is an explicit call to trigger a deployer action
     * the consumer is subscribed for notifications which will have an initial state
     * followed by an end state when the app changes are complete
     * @param input: @ITCIApplicationDeployRequest
     * @param callback
     * @return @Subject<ITCIApplicationDeployResponse>
     */
    abstract pushAppChanges(input: ITCIApplicationDeployRequest,
                            callback: (cinput: ITCIApplicationDeployRequest)
                                => Observable<ITCIApplicationDeployResponse>): Observable<ITCIApplicationDeployResponse>;

    /**
     * Return state if the provider is busy waiting for the pushAppChanges to finish
     * @param input
     */
    abstract isPushingAppChanges(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Return an observable indicating if the deployer is busy or not
     * The deployer is busy when pushingAppChanges/start/stop/scale is happening
     * and also when it gets a busy indication from polling async app manager
     * app status for tci apps and it is the responsibility of the tenant provider
     * to let the TCI infrastructure know when it is busy
     * @param input
     */
    abstract isBusy(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Return an observable<boolean> if the UI should behave in autoscaling mode
     * In this mode only the number of instances are to be captured and saved
     * in domain server for TCI apps and API orchestrator needs to turn on
     * auto scaling mode for existing / running apps. For Scribe there is no
     * Autoscaling and it should be false always
     * @param input
     */
    abstract canAutoScale(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * update auto scale configuration
     * @param input
     */
    abstract updateAutoScale(input: ITCIApplicationDeployRequest): void;

    /**
     * This is an application deployment state snapshot at any given point of time.
     * The caller should not make state dependant UI changes using this state as
     * it will dynamically change.
     * @param app Application instance requesting the data
     */
    abstract getStatusSnapshot(app: ITCIApplication): ITCIApplicationDeployResponse;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    abstract startStatusSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    abstract stopStatusSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Returns deploymentStatus Subject, the UI consumer will subscribe and unsubscribe from this
     * subject when the UI is active and inactive respectively. The provider's responsibility is to
     * control how frequently it needs to emit on the subject. There is only one subject per provider
     * which will emit per App status change with app Id in the @ITCIApplicationDeployResponse. The
     * subscriber will update the respective app status based on the response.
     *
     * Important: It's tenant responsibility to catch errors that might happen during the status check
     * for this subject to return data reflecting such cases.
     *
     * @return Subject<ITCIApplicationDeployResponse>
     */
    abstract getStatusSubject(): Subject<ITCIApplicationDeployResponse>;

    /**
     * checks if the provider can scale apps
     * @param tciTenantAppType
     * @return @Observable<boolean>
     */
    abstract canScale(input: ITCIApplicationDeployRequest): Observable<boolean>;


    /**
     * Sets number of instances by given count i.e. adds to or subtract from the existing number of instances
     * The number of instances can range from 0 to max allowed instances
     * @return @Observable<IApplicationDeployResponse>
     */
    abstract setInstances(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * Gets the instance scaling data for initialization
     * @param input
     */
    abstract getInstances(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * checks if the details badge can be shown.
     * The details  badge will appear and onclick it will open a popup
     * which will load a tenant component implementing
     * TCI_APP_LIST_DETAIL_BADGE = new TCITenantSelectorType("tci-app-list-detail-badge");
     * @param input
     */
    abstract canShowDetails(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * The details popup is dynamic component loader which needs input to pass
     * to the tenant component to initialize. Each tenant provider will determing what input
     * it needs by extending ITCITenantComponentInput to its component input binding.
     * @param input
     * @deprecated
     */
    abstract getDetailsInput<T extends ITCITenantComponentInput>(input: ITCIApplicationDeployRequest): Observable<T>;

    /**
     * The details popup will no longer be a dynamic tenant component. The popup component will
     * display the HTML content returned by this function. The HTML content cannot have any forms
     * events, actions. Any resource links like images need to have absolute URLS resolved by the
     * tenant microservice static files.
     * @param input
     */
    abstract getDetailContent(input: ITCIApplicationDeployRequest): Observable<string | ITCIApplicationDeploymentMiniDetails>;

    /**
     * The details popup overlay includes a button for the user to click and copy the content
     * into the clipboard. This method returns the text that would be copied.
     * The text should not include HTML tags.
     * @param input
     */
    abstract getDetailTextForClipboard(input: ITCIApplicationDeployRequest): Observable<string>;

    /**
     * Check if there is a status error to show the red dot badge on status.
     * @param input
     */
    abstract showStatusError(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * This will be triggered when "pushAppChanges" callback have finished the
     * execution with the intention of restarting the deployment state check
     */
    abstract refresh(input: ITCIApplicationDeployRequest): void;

    /**
     * Returns the Subject that would emit the status of the application to
     * notify UI. The check loop would call "next" on the subject on status change
     * @param request
     */
    abstract getOnPremiseAppStatusSubject(request: ITCIApplicationDeployRequest): Subject<ITCIApplicationDeployResponse>;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    abstract startOnPremiseStatusSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    abstract stopOnPremiseStatusSubscription(app: ITCIApplication): Observable<boolean>;

    abstract getOnPremiseApplicationStatusSubject(): Subject<ITCIApplicationDeployResponse>;
}


@Injectable()
export abstract class BaseTCIConnectionServiceProvider extends TCITenantServiceProvider implements ITCIConnectionServiceProvider {
    constructor(@Inject(Injector) private _injector) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.CONNECTION_SERVICE);
    }

    /**
     * returns list of connectors
     * This is a override mechanism to let flogo tenant
     * provide connectors via UI service code from studio cache.
     * For scribe the base class should call WS /connectors API
     */
    abstract getConnectors(): Observable<ITCIConnector[]>;

    /**
     * Returns a list of connections from individual provider
     * @return @Observable<ITCIConnection[]>
     */
    abstract getConnections(): Observable<ITCIConnection[]>;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param request: @ITCIConnectionRequest
     * @return @Observable<boolean>
     */
    abstract startConnectionSubscription(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param request: @ITCIConnectionRequest
     * @return @Observable<boolean>
     */
    abstract stopConnectionSubscription(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Returns connections Subject, the UI consumer will subscribe and unsubscribe from this
     * subject when the UI is active and inactive respectively. The provider's responsibility is to
     * control how frequently it needs to emit on the subject. There is only one subject per provider
     * which will emit per connection status change with ITCIConnection in the @ITCIConnectionServiceResponse. The
     * subscriber will update the respective app status based on the response
     * @return Subject<ITCIConnectionResponse>
     */
    abstract getConnectionsSubject(): Subject<ITCIConnectionResponse>;

    /**
     * check before creating a connection using the editor
     * @param request
     */
    abstract canCreate(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * create the connection by using the router which can be injected on the
     * tenant implementation
     * @param request
     * @param returnUrl
     */
    abstract createConnection(request: ITCIConnectionRequest, returnUrl: string): Observable<ITCIConnectionResponse>;

    /**
     * check before opening a connection editor for editing
     * @param request
     */
    abstract canEdit(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * edit the connection by using the router which can be injected on the
     * tenant implementation
     * @param request
     * @param returnUrl
     */
    abstract editConnection(request: ITCIConnectionRequest, returnUrl: string): Observable<ITCIConnectionResponse>;

    /**
     * check if the tenant supports the connectorType
     * @param tenantType
     */
    abstract canSupport(connectorType: ITCIConnector): Observable<boolean>;

    /**
     * get connected app count
     * @param request
     */
    abstract getConnectedAppCount(request: ITCIConnectionRequest): Observable<number>;

    /**
     * Show connected apps if allowed by the tenant
     * @param request
     */
    abstract showConnectedApps(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Get connected app id'ss for the given connection id
     * @param request
     */
    abstract getConnectedApps(request: ITCIConnectionRequest): Observable<string[]>;

    /**
     * check if the connection can be deleted
     * @param request
     */
    abstract canDelete(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * delete Connection
     * @param connection
     */
    abstract deleteConnection(request: ITCIConnectionRequest): Observable<ITCIConnectionResponse>;

    /**
     * Show tooltip text only when canDelete() == false
     * @param request
     */
    abstract getDeleteTooltip(request: ITCIConnectionRequest): Observable<string>;


    /**
     * Indicate if this connection has status which may put a badge on the connection in
     * connection list page
     */
    abstract hasConnectionStatus(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Get the connection status msg for display
     * @param request
     */
    abstract getConnectionStatus(request: ITCIConnectionRequest): Observable<ITCIConnectionResponse>;

    /**
     * Disconnected Components that take actions related to a connection
     * which impacts the Connection list UI state need to enclose the
     * actions within the callback so the connection provider can let
     * other dependencies get notified on the completion of the action
     * for performing refresh or any other action
     * @param input
     * @param callback
     */
    abstract pushConnectionChanges(
        input: ITCIConnectionRequest,
        callback: (cinput: ITCIConnectionRequest) => Observable<ITCIConnectionResponse>);


    /**
     * Return state if the provider is busy waiting for connection actions to finish
     * @param input
     */
    abstract isBusy(input: ITCIConnectionRequest): Observable<boolean>;

    /**
     * The UI will show/hide visibility column based on this result true/false
     * The tenants e.g. Flogo TCI and Flogo Enterprise will have different results
     * @param input
     * @deprecated
     */
    abstract canShowConnectionVisibility(input: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Return the visibility status for a given {@ITCIConnection}
     * The changing of the visibility from hamburger menu code needs to be
     * wrapped inside pushConnectionChanges() which will trigger a refresh
     * of the Connection List via the NOTIFICATION_REFRESH and conn list
     * will call this provider function to get individual connection visibility
     * status
     * @param input
     */
    abstract getConnectionVisibility(input: ITCIConnectionRequest): Observable<ITCIConnectionResponse>;
}

/************************************************************************************************/
export abstract class BaseMarketplaceProvider extends TCITenantServiceProvider implements ITCIMarketplaceServiceProvider {
    constructor(@Inject(Injector) private _injector) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.MARKETPLACE);
    }

    /**
     * Get list of market place connectors provided by the tenant
     */
    abstract getConnectors(): Observable<ITCIMarketplaceConnector[]>;

    /**
     * Get a single connector data
     * @param input
     */
    abstract getConnectorById(input: ITCIMarketplaceConnectorRequest): Observable<ITCIMarketplaceConnector>;

    /**
     * Tenant providers decides if the connector can be installed
     * @param input
     */
    abstract canInstallConnector(input: ITCIMarketplaceConnectorRequest): Observable<boolean>;

    /**
     * @param input
     */
    abstract installConnector(input: ITCIMarketplaceConnectorRequest): Observable<ITCIMarketplaceConnectorResponse>;

    /**
     * Tenant providers decides if the connector can be uninstalled
     * @param input
     */
    abstract canUninstallConnector(input: ITCIMarketplaceConnectorRequest): Observable<boolean>;

    /**
     * install connector
     * @param input
     */
    abstract uninstallConnector(input: ITCIMarketplaceConnectorRequest): Observable<ITCIMarketplaceConnectorResponse>;

    /**
     * Check if the connector is enabled
     * @param input
     */
    abstract isConnectorEnabled(input: ITCIMarketplaceConnectorRequest): Observable<boolean>;

    /**
     * Return the catalog of the connector supported technologies
     * @param input
     */
    abstract getSuportedTechs(input: ITCIMarketplaceConnectorRequest): Observable<TCIMarketplaceSupportedTechs[]>;
}

/******************************************************************************************************************/
/**
 * BaseSupplementProvider
 */
export abstract class BaseSupplementProvider extends TCITenantServiceProvider implements ITCISupplementServiceProvider {
    constructor(@Inject(Injector) private _injector) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.SUPPLEMENTS);
    }

    /**
     * Check before calling uploadSupplement
     * @param input
     */
    abstract canCreate(input: ITCITenantSupplementRequest): Observable<boolean | ITCIValidationResult>;

    /**
     * Validate before uploading if there are content errors
     * The validation result can be an error or warning
     * @param input
     */
    abstract validate(input: ITCITenantSupplementRequest): Observable<ITCIValidationResult>;

    /**
     * upload Supplement
     * @param input
     */
    abstract createSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse>;

    /**
     * Return a list of tenant specific supplements, the UI will use this to get supplements before
     * doing aggregation of the combined list, one from each tenant
     */
    abstract getSupplements(): Observable<ITCISupplement[]>;

    /**
     * Get List of Supplement Connectors
     * @param input
     */
    abstract getAllSupplementConnectors(input: ITCITenantSupplementRequest): Observable<ITCISupplementConnector[]>;


    /**
     * Get List of Connected Applications
     * @param input
     */
    abstract getConnectedAppsById(input: ITCITenantSupplementRequest): Observable<ITCIApplication[]>;

    /**
     * download Supplement will start downloading one or more files
     * @param input
     */
    abstract downloadSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse>;

    /**
     * Check before calling updateSupplement
     * @param input
     */
    abstract canUpdate(input: ITCITenantSupplementRequest): Observable<boolean | ITCIValidationResult>;

    /**
     * update Supplement
     * @param input
     */
    abstract updateSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse>;

    /**
     * Check before calling deleteSupplement
     * @param input
     */
    abstract canDelete(input: ITCITenantSupplementRequest): Observable<boolean | ITCIValidationResult>;

    /**
     * delete Supplement
     * @param input
     */
    abstract deleteSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse>;

    /**
     * Status subject
     */
    abstract getStatusSubject(): Subject<ITCITenantSupplementResponse>;

    /**
     * This is an explicit call to trigger a action
     * the consumer is subscribed for notifications which will have an initial state
     * followed by an end state when the supp changes are complete
     * @param input: @ITCITenantSupplementRequest
     * @param callback
     * @return @Subject<ITCITenantSupplementResponse>
     */
    abstract pushSuppChanges(input: ITCITenantSupplementRequest,
                             callback: (cinput: ITCITenantSupplementRequest) =>
                                 Observable<ITCITenantSupplementResponse>): Observable<ITCITenantSupplementResponse>;

    /**
     * Return state if the provider is busy waiting for the pushSuppChanges to finish
     * @param input
     */
    abstract isBusy(input: ITCITenantSupplementRequest): Observable<boolean>;
}

/**
 * BaseTCIApplicationExecHistoryProvider
 */
@Injectable()
export abstract class BaseTCIApplicationExecHistoryProvider extends TCITenantServiceProvider implements ITCIApplicationExecHistoryProvider {
    constructor(@Inject(Injector) private _injector?: Injector, @Inject(Http) private _http?: Http) {
        super(TCI_TENANT_SERVICE_PROVIDER_TYPE.EXEC_HISTORY);
    }
    /**
     * get injector
     * @protected
     */
    protected getInjector(): Injector {
        return this._injector;
    }

    /**
     * get http
     * @protected
     */
    protected getHttp(): Http {
        return this._http;
    }

    /**
     * Get execution history list
     * @param appType: application type
     * @param appId: application Id
     * @param processStartTime: start time
     * @param processEndTime: end time
     * @param page: page number
     * @param limit: limit per page
     * @param processName: execution history item process name
     * @param status: execution history item status
     * @param searchText: search text
     * @param sortColumn: sortable column, default is startTime
     * @param sortDesc: sort order, default is true, descending
     */
    abstract getExecHistory(appType: TCITenantAppType, appId: string,
                            processStartTime: number, processEndTime: number,
                            page?: number, limit?: number,
                            processName?: string, status?: TCIExecHistoryStatus, searchText?: string,
                            sortColumn?: TCIExecHistorySortableColumn, sortDesc?: boolean): Observable<ITCIExecHistoryResponse>;
    /**
     * Get execution history list
     * @param appType: application type
     * @param appId: application Id
     * @param processStartTime: start time
     * @param processEndTime: end time
     * @param processName: execution history item process name
     * @param appNodeName: app instance id
     * @param processInstanceJobId: process instance job id
     * @param sortColumn: sortable column, default is startTime
     * @param sortDesc: sort order, default is true, descending
     */
    abstract getExecHistoryChildListByJobId(
        appType: TCITenantAppType, appId: string,
        processStartTime: number, processEndTime: number,
        processName: string, appNodeName: string, processInstanceJobId: string,
        sortColumn?: TCIExecHistorySortableColumn, sortDesc?: boolean
    ): Observable<ITCIExecHistoryProcessResponse>;

    /**
     * Get execution history process list
     * @param appType: application type
     * @param appId: application Id
     * @param processStartTime: process start time
     * @param processEndTime: process end time
     */
    abstract getExecHistoryProcessList(
        appType: TCITenantAppType, appId: string,
        processStartTime: number, processEndTime: number
    ): Observable<ITCIExecHistoryProcessListResponse>;

    /**
     * Get execution history detail
     * @param appType: application type
     * @param appId: application Id
     * @param processName: execution history item process name
     * @param appNodeName: app instance id
     * @param processInstanceJobId: process instance job id
     * @param processStartTime: process start time
     * @param processEndTime: process end time
     */
    abstract getExecHistoryDetail(
        appType: TCITenantAppType, appId: string,
        processName: string, appNodeName: string, processInstanceJobId: string,
        processStartTime: number, processEndTime: number
    ): Observable<ITCIExecHistoryDetailResponse>;
}
